import { type TellescopeModelFromName } from "@tellescope/tanstack-query";
import { differenceInCalendarDays } from "date-fns/differenceInCalendarDays";

type InboxThreadRecord = TellescopeModelFromName<"inbox_threads">;

/**
 * Get the start of day in UTC
 */
function startOfDayUTC(date: Date): Date {
  const d = new Date(date);
  d.setUTCHours(0, 0, 0, 0);
  return d;
}

/**
 * Get the start of week in UTC (Sunday = 0)
 */
function startOfWeekUTC(date: Date): Date {
  const d = new Date(date);
  const day = d.getUTCDay();
  const diff = day; // Days since Sunday
  d.setUTCDate(d.getUTCDate() - diff);
  d.setUTCHours(0, 0, 0, 0);
  return d;
}

/**
 * Get the start of month in UTC
 */
function startOfMonthUTC(date: Date): Date {
  const d = new Date(date);
  d.setUTCDate(1);
  d.setUTCHours(0, 0, 0, 0);
  return d;
}

export type TimestampField = "timestamp" | "outboundTimestamp";

export interface GroupThreadsByDateOptions {
  /**
   * Which timestamp field to use for grouping
   * @default "timestamp"
   */
  timestampField?: TimestampField;
}

export interface ThreadGroup<T = InboxThreadRecord> {
  /**
   * ISO string representing the start of this group period
   */
  groupKey: string;
  /**
   * Threads in this group, sorted by the timestamp field (newest first)
   */
  threads: T[];
}

/**
 * Groups inbox threads by date with progressively wider groupings:
 * - < 7 days old: grouped by day
 * - < 30 days old: grouped by week
 * - < 365 days old: grouped by month
 * - >= 365 days old: all grouped together
 *
 * @param threads - Array of InboxThreadRecord objects (or partial objects with required fields) to group.
 *                  IMPORTANT: Assumes the array is already sorted by the timestampField (newest first).
 * @param options - Configuration options for grouping
 * @returns Array of ThreadGroup objects, sorted from newest to oldest
 */
export function groupThreadsByDate<T extends Pick<InboxThreadRecord, "timestamp" | "outboundTimestamp">>(
  threads: T[],
  options: GroupThreadsByDateOptions = {}
): ThreadGroup<T>[] {
  const { timestampField = "timestamp" } = options;
  const now = new Date();

  // Filter out threads without the specified timestamp (assumes already sorted newest first)
  const validThreads = threads.filter((thread) => thread[timestampField]);

  // Group threads by their appropriate time bucket
  const groups = new Map<string, T[]>();

  validThreads.forEach((thread) => {
    const threadDate = new Date(thread[timestampField] as Date | string);
    const daysOld = differenceInCalendarDays(now, threadDate);

    let groupKey: string;

    if (daysOld < 0) {
      // Future dates (edge case) - treat as today
      groupKey = startOfDayUTC(now).toISOString();
    } else if (daysOld < 7) {
      // Group by day
      groupKey = startOfDayUTC(threadDate).toISOString();
    } else if (daysOld < 30) {
      // Group by week
      groupKey = startOfWeekUTC(threadDate).toISOString();
    } else if (daysOld < 365) {
      // Group by month
      groupKey = startOfMonthUTC(threadDate).toISOString();
    } else {
      // All threads >= 365 days old grouped together
      groupKey = "older-than-year";
    }

    if (!groups.has(groupKey)) {
      groups.set(groupKey, []);
    }
    groups.get(groupKey)!.push(thread);
  });

  // Convert map to array of ThreadGroup objects
  const result: ThreadGroup<T>[] = Array.from(groups.entries()).map(([groupKey, threads]) => ({
    groupKey,
    threads,
  }));

  // Sort groups by groupKey (newest first), with special handling for "older-than-year"
  result.sort((a, b) => {
    if (a.groupKey === "older-than-year") return 1; // Always last
    if (b.groupKey === "older-than-year") return -1; // Always last
    return new Date(b.groupKey).getTime() - new Date(a.groupKey).getTime();
  });

  return result;
}
